// Las categorías de la tienda: el árbol y el selector de la ficha —
// ADR-2026-09-07-categorias-de-tienda.
//
// Son dos pantallas de la MISMA lista, y por eso viven en el mismo archivo: el
// gestor deja armar el árbol y el selector deja atarle un producto. Si se
// separaran, la indentación, el plegado y el orden habría que escribirlos dos
// veces y se irían despegando.
import { boton } from './botones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, FolderPlus, Loader2, Pencil, Plus, Tag, Trash2, X } from 'lucide-react';
import { SheetLateral } from '../../components/SheetLateral';
import PopoverAnclado from './PopoverAnclado';
import {
  aplanarArbol,
  caminoDe,
  destinosPosibles,
  hermanaRepetida,
  ramaDe,
} from './arbolDeCategorias';
import type { CategoriaDeTienda } from './lanzamientosApi';

/** Cuánto se corre cada nivel. Suficiente para leerse, poco para no quedarse sin ancho. */
const SANGRIA = 20;

// ── Alta ───────────────────────────────────────────────────────────────────

/**
 * El campo para sumar una categoría. Vive abierto o cerrado según `madre`:
 * `undefined` = cerrado, `null` = va al primer nivel, un id = va adentro de esa.
 *
 * El aviso de nombre repetido se calcula mientras se escribe y no al mandar: la
 * base lo rechaza igual, pero enterarse después de apretar es enterarse tarde.
 */
function AltaDeCategoria({
  categorias,
  madre,
  onCancelar,
  onCrear,
}: {
  categorias: readonly CategoriaDeTienda[];
  madre: string | null;
  onCancelar: () => void;
  onCrear: (nombre: string, madre: string | null) => Promise<void>;
}) {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const repetida = hermanaRepetida(categorias, madre, nombre);
  const puede = nombre.trim() !== '' && !repetida && !guardando;

  const mandar = async () => {
    if (!puede) return;
    setGuardando(true);
    await onCrear(nombre.trim(), madre);
    setGuardando(false);
  };

  return (
    <div className="py-2" style={{ paddingLeft: madre ? SANGRIA : 0 }}>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={nombre}
          placeholder={madre ? 'Nombre de la subcategoría' : 'Nombre de la categoría'}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void mandar();
            if (e.key === 'Escape') onCancelar();
          }}
          className={`min-w-0 flex-1 rounded-xl border py-2 px-3 text-sm text-brand-black outline-none ${
            repetida ? 'border-amber-400' : 'border-neutral-200 focus:border-neutral-400'
          }`}
        />
        <button
          type="button"
          disabled={!puede}
          onClick={() => void mandar()}
          className={boton('primario', { extra: 'shrink-0 py-2 px-4' })}
        >
          {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Crear
        </button>
        <button
          type="button"
          onClick={onCancelar}
          aria-label="Cancelar"
          className="shrink-0 rounded-lg p-2 text-neutral-400 hover:bg-neutral-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {repetida && (
        <p className="mt-1 text-[11px] text-amber-700">
          Ya tenés una categoría con ese nombre en el mismo lugar.
        </p>
      )}
    </div>
  );
}

// ── El gestor ──────────────────────────────────────────────────────────────

/** Menú de una categoría: renombrar, mover de madre, borrar. */
function AccionesDeCategoria({
  categoria,
  categorias,
  onRenombrar,
  onMover,
  onBorrar,
}: {
  categoria: CategoriaDeTienda;
  categorias: readonly CategoriaDeTienda[];
  onRenombrar: () => void;
  onMover: (parentId: string | null) => void;
  onBorrar: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [moviendo, setMoviendo] = useState(false);
  const botonRef = useRef<HTMLButtonElement | null>(null);

  // Ella misma y su descendencia no son destinos: meterla adentro de una hija
  // suya cierra un círculo y el árbol se queda sin raíz.
  const destinos = useMemo(
    () => destinosPosibles(categorias, categoria.id),
    [categorias, categoria.id],
  );

  return (
    <>
      <button
        ref={botonRef}
        type="button"
        onClick={() => {
          setMoviendo(false);
          setAbierto((v) => !v);
        }}
        aria-label={`Opciones de ${categoria.nombre}`}
        className="shrink-0 rounded-lg p-1.5 text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <PopoverAnclado
        abierto={abierto}
        anclaRef={botonRef}
        onCerrar={() => setAbierto(false)}
        alineacion="derecha"
        ancho={232}
        capa={230}
      >
        {moviendo ? (
          <>
            <p className="border-b border-neutral-100 py-2 px-3 text-[11px] text-neutral-500">
              Mover adentro de…
            </p>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onMover(null);
              }}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Sin madre (primer nivel)
            </button>
            {destinos.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  setAbierto(false);
                  onMover(d.id);
                }}
                className="block w-full truncate py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
              >
                {caminoDe(categorias, d.id)}
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onRenombrar();
              }}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Renombrar
            </button>
            <button
              type="button"
              onClick={() => setMoviendo(true)}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Mover a otra categoría
            </button>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onBorrar();
              }}
              className="block w-full border-t border-neutral-100 py-2.5 px-3 text-left text-sm text-red-600 hover:bg-red-50"
            >
              Borrar
            </button>
          </>
        )}
      </PopoverAnclado>
    </>
  );
}

/**
 * El panel para armar el árbol. Se abre desde el lanzamiento y desde la ficha.
 *
 * Es un `SheetLateral` y no una pantalla aparte porque armar categorías es algo
 * que se hace EN MEDIO de completar una ficha: mandar al usuario a otra ruta le
 * haría perder de vista el producto que estaba cargando.
 */
/**
 * El árbol: la lista indentada, plegable, con el botón para crear una
 * subcategoría adentro de cualquier fila y el de crear una de primer nivel
 * abajo.
 *
 * Está acá y no adentro del panel porque lo usan dos pantallas: el panel donde
 * se editan las categorías y el que elige a cuál van los productos
 * seleccionados. Que se vean igual no es coincidencia — son la misma lista, y
 * dos versiones parecidas se despegan a la primera corrección.
 *
 * Lo único que cambia entre las dos es qué se hace con una fila: el panel deja
 * renombrarla, el otro la elige. Eso entra por `renderNombre` y `acciones`.
 */
export function ArbolDeCategorias({
  categorias,
  onCrear,
  renderNombre,
  acciones,
  deshabilitado,
  vacio,
}: {
  categorias: CategoriaDeTienda[];
  onCrear: (nombre: string, madre: string | null) => Promise<void>;
  /** Qué se dibuja donde va el nombre. Por defecto, el nombre. */
  renderNombre?: (c: CategoriaDeTienda) => React.ReactNode;
  /** Botones al final de la fila. */
  acciones?: (c: CategoriaDeTienda) => React.ReactNode;
  deshabilitado?: boolean;
  /** Qué mostrar cuando no hay ninguna. */
  vacio?: React.ReactNode;
}) {
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set());
  const [creandoEn, setCreandoEn] = useState<string | null | undefined>(undefined);

  const filas = useMemo(() => aplanarArbol(categorias, plegadas), [categorias, plegadas]);

  const plegar = (id: string) =>
    setPlegadas((p) => {
      const s = new Set(p);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const crear = async (nombre: string, madre: string | null) => {
    await onCrear(nombre, madre);
    setCreandoEn(undefined);
  };

  return (
    <div>
      {filas.length === 0 && creandoEn === undefined && vacio}

      <ul>
        {filas.map(({ categoria: c, nivel, tieneHijas }) => (
          <li key={c.id}>
            <div
              className="group flex items-center gap-1 rounded-lg py-1.5 pr-1 hover:bg-neutral-50"
              style={{ paddingLeft: nivel * SANGRIA }}
            >
              <button
                type="button"
                disabled={!tieneHijas}
                onClick={() => plegar(c.id)}
                aria-label={plegadas.has(c.id) ? 'Desplegar' : 'Plegar'}
                className="shrink-0 rounded p-0.5 text-neutral-400 disabled:opacity-0"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${
                    tieneHijas && !plegadas.has(c.id) ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {renderNombre ? (
                renderNombre(c)
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm text-brand-black">{c.nombre}</span>
              )}

              <button
                type="button"
                disabled={deshabilitado}
                onClick={() => setCreandoEn(c.id)}
                aria-label={`Agregar una subcategoría en ${c.nombre}`}
                title="Agregar una subcategoría"
                className="shrink-0 rounded-lg p-1.5 text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100 disabled:opacity-0"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
              {acciones?.(c)}
            </div>

            {creandoEn === c.id && (
              <div style={{ paddingLeft: (nivel + 1) * SANGRIA }}>
                <AltaDeCategoria
                  categorias={categorias}
                  madre={c.id}
                  onCancelar={() => setCreandoEn(undefined)}
                  onCrear={crear}
                />
              </div>
            )}
          </li>
        ))}
      </ul>

      {creandoEn === null ? (
        <AltaDeCategoria
          categorias={categorias}
          madre={null}
          onCancelar={() => setCreandoEn(undefined)}
          onCrear={crear}
        />
      ) : (
        <button
          type="button"
          disabled={deshabilitado}
          onClick={() => setCreandoEn(null)}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-300 py-2.5 text-xs text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva categoría
        </button>
      )}
    </div>
  );
}

export function PanelDeCategorias({
  abierto,
  categorias,
  cargando,
  onCerrar,
  onCrear,
  onRenombrar,
  onMover,
  onBorrar,
}: {
  abierto: boolean;
  categorias: CategoriaDeTienda[];
  cargando: boolean;
  onCerrar: () => void;
  onCrear: (nombre: string, madre: string | null) => Promise<{ error: string | null }>;
  onRenombrar: (id: string, nombre: string) => Promise<string | null>;
  onMover: (id: string, parentId: string | null) => Promise<string | null>;
  onBorrar: (id: string) => Promise<string | null>;
}) {
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [nombreEditado, setNombreEditado] = useState('');
  const [porBorrar, setPorBorrar] = useState<CategoriaDeTienda | null>(null);
  const [error, setError] = useState<string | null>(null);

  const conError = async (accion: () => Promise<string | null>) => {
    setError(await accion());
  };

  // Cuántas se lleva puestas el borrado: es la diferencia entre "borrás una" y
  // "borrás tu catálogo entero", y hay que decirlo ANTES.
  const seLleva = porBorrar ? ramaDe(categorias, porBorrar.id).size : 0;

  return (
    <SheetLateral open={abierto} onClose={onCerrar} titulo="Categorías de la tienda">
      <div className="p-4">
        <p className="text-xs leading-relaxed text-neutral-500">
          Son las de tu tienda, no las de este lanzamiento: se comparten entre todos. Una
          subcategoría es una categoría adentro de otra.
        </p>

        {error && (
          <p className="mt-3 rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{error}</p>
        )}

        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="mt-3">
            <ArbolDeCategorias
              categorias={categorias}
              onCrear={async (nombre, madre) => {
                setError((await onCrear(nombre, madre)).error);
              }}
              vacio={
                <div className="rounded-2xl border border-dashed border-neutral-300 py-10 px-4 text-center">
                  <Tag className="mx-auto w-6 h-6 text-neutral-300" />
                  <p className="mt-3 text-sm font-medium text-brand-black">
                    Todavía no tenés ninguna
                  </p>
                  <p className="mx-auto mt-1 max-w-xs text-xs text-neutral-500">
                    Las categorías son la navegación de tu tienda: “Remeras”, y adentro
                    “Oversize”.
                  </p>
                </div>
              }
              renderNombre={(c) =>
                renombrando === c.id ? (
                  <input
                    autoFocus
                    value={nombreEditado}
                    onChange={(e) => setNombreEditado(e.target.value)}
                    onBlur={() => setRenombrando(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setRenombrando(null);
                      if (e.key === 'Enter') {
                        const v = nombreEditado.trim();
                        setRenombrando(null);
                        if (v && v !== c.nombre) void conError(() => onRenombrar(c.id, v));
                      }
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-neutral-300 py-1 px-2 text-sm text-brand-black outline-none"
                  />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-sm text-brand-black">
                    {c.nombre}
                  </span>
                )
              }
              acciones={(c) => (
                <AccionesDeCategoria
                  categoria={c}
                  categorias={categorias}
                  onRenombrar={() => {
                    setNombreEditado(c.nombre);
                    setRenombrando(c.id);
                  }}
                  onMover={(parentId) => void conError(() => onMover(c.id, parentId))}
                  onBorrar={() => setPorBorrar(c)}
                />
              )}
            />
          </div>
        )}
      </div>

      {/* Confirmar el borrado dentro del panel: decir cuántas se lleva es el
          dato que hace falta para decidir. */}
      {porBorrar &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-6 sm:rounded-3xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600">
                <Trash2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-brand-black">
                  ¿Borrar “{porBorrar.nombre}”?
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-neutral-500">
                  {seLleva > 1
                    ? `Se borra con sus ${seLleva - 1} subcategoría${seLleva - 1 === 1 ? '' : 's'}. Los productos que las tenían quedan sin esa categoría.`
                    : 'Los productos que la tenían quedan sin esa categoría.'}
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setPorBorrar(null)}
                className={boton('neutro', { extra: 'flex-1' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = porBorrar.id;
                  setPorBorrar(null);
                  void conError(() => onBorrar(id));
                }}
                className="flex-1 rounded-xl bg-red-600 py-2.5 px-5 text-sm font-medium text-white hover:bg-red-700"
              >
                Borrar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </SheetLateral>
  );
}

// ── El selector de la ficha ────────────────────────────────────────────────

/**
 * Qué categorías lleva este producto. El mismo árbol, con casillas.
 *
 * Marcar una NO marca a su madre. En Tiendanube un producto pertenece a las
 * categorías que le pongas y a ninguna más: si estar en "Oversize" pusiera
 * también "Remeras", el usuario no podría decir "solo en la subcategoría", que
 * es una cosa que se quiere decir.
 */
export function SelectorDeCategorias({
  categorias,
  elegidas,
  onCambiar,
  onAbrirPanel,
}: {
  categorias: CategoriaDeTienda[];
  elegidas: readonly string[];
  onCambiar: (id: string, puesta: boolean) => void;
  onAbrirPanel: () => void;
}) {
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set());
  const filas = useMemo(() => aplanarArbol(categorias, plegadas), [categorias, plegadas]);
  const puestas = useMemo(() => new Set(elegidas), [elegidas]);

  const plegar = useCallback(
    (id: string) =>
      setPlegadas((p) => {
        const s = new Set(p);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        return s;
      }),
    [],
  );

  if (categorias.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 py-6 px-4 text-center">
        <p className="text-xs text-neutral-500">
          Todavía no armaste las categorías de tu tienda.
        </p>
        <button
          type="button"
          onClick={onAbrirPanel}
          className="mt-2 text-xs font-medium text-brand-black underline"
        >
          Armarlas ahora
        </button>
      </div>
    );
  }

  return (
    <div>
      <ul className="max-h-72 overflow-auto">
        {filas.map(({ categoria: c, nivel, tieneHijas }) => (
          <li key={c.id}>
            <div
              className="flex items-center gap-1 rounded-lg py-1 hover:bg-neutral-50"
              style={{ paddingLeft: nivel * SANGRIA }}
            >
              <button
                type="button"
                disabled={!tieneHijas}
                onClick={() => plegar(c.id)}
                aria-label={plegadas.has(c.id) ? 'Desplegar' : 'Plegar'}
                className="shrink-0 rounded p-0.5 text-neutral-400 disabled:opacity-0"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 transition-transform ${
                    tieneHijas && !plegadas.has(c.id) ? 'rotate-90' : ''
                  }`}
                />
              </button>
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={puestas.has(c.id)}
                  onChange={(e) => onCambiar(c.id, e.target.checked)}
                  className="h-4 w-4 shrink-0 rounded border-neutral-300 accent-[#1644EB]"
                />
                <span className="min-w-0 truncate text-sm text-brand-black">{c.nombre}</span>
              </label>
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onAbrirPanel}
        className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500 hover:text-brand-black"
      >
        <Plus className="w-3.5 h-3.5" /> Crear o editar categorías
      </button>
    </div>
  );
}
